import React from 'react';
import { Mail, Linkedin, Sparkles, Zap, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { personalInfo } from '../data/mock';

const ContactNew = () => {
  return (
    <section id="contact" className="py-20 bg-[#f5f0e6] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-gradient-to-br from-[#d9b896]/30 to-transparent blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-gradient-to-br from-[#e0d0b8]/40 to-transparent blur-3xl"></div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <p className="font-handwriting text-4xl mb-2 gradient-text">Let's make something great</p>
          <h2 className="text-4xl md:text-6xl font-bold text-[#2d2a26] mb-4">
            <span className="font-serif">Get in </span>
            <span className="font-display gradient-text-animated">Touch</span>
          </h2>
        </div>

        {/* Main Contact Card */}
        <Card data-testid="contact-card" className="bg-white/80 backdrop-blur-sm border-[#e0d0b8] overflow-hidden hover:shadow-xl transition-all duration-300">
          <div className="p-8 md:p-12">
            {/* Top Section */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-12">
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#b88865]/15 to-[#6b4226]/15 text-[#6b4226] px-4 py-2 rounded-full text-sm font-medium mb-4 border border-[#8b5a3c]/25">
                  <Sparkles className="w-4 h-4" />
                  Available for opportunities
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-[#2d2a26] mb-3">
                  <span className="font-serif">Let's </span>
                  <span className="font-display gradient-text">connect</span>
                </h3>
                <p className="text-[#555555] text-lg">
                  Whether it's about a role, a project, or just a chat about product, automation, or strategy - would love to connect!
                </p>
              </div>

              <div className="flex-shrink-0">
                <div className="w-32 h-32 rounded-full gradient-bg flex items-center justify-center shadow-xl">
                  <Send className="w-16 h-16 text-white" />
                </div>
              </div>
            </div>

            {/* Contact Methods */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <Card className="bg-[#fffcf6]/80 border-[#e0d0b8] hover:border-[#8b5a3c]/40 transition-all duration-300 group cursor-pointer hover:shadow-lg hover:-translate-y-1">
                <a href={`mailto:${personalInfo.email}`} data-testid="contact-email-link" className="block p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl gradient-bg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                      <Mail className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-[#2d2a26] font-semibold mb-1 group-hover:text-[#8b5a3c] transition-colors">Email Me</h4>
                      <p className="text-sm text-[#737373] break-all">{personalInfo.email}</p>
                    </div>
                  </div>
                </a>
              </Card>

              <Card className="bg-[#fffcf6]/80 border-[#e0d0b8] hover:border-[#8b5a3c]/40 transition-all duration-300 group cursor-pointer hover:shadow-lg hover:-translate-y-1">
                <div
                  data-testid="contact-linkedin-link"
                  onClick={() => window.open(personalInfo.linkedin, '_blank', 'noopener,noreferrer')}
                  className="block p-6"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl gradient-bg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                      <Linkedin className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-[#2d2a26] font-semibold mb-1 group-hover:text-[#8b5a3c] transition-colors">Connect on LinkedIn</h4>
                      <p className="text-sm text-[#737373]">View my full profile</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Quick CTA Buttons */}
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                size="lg"
                data-testid="contact-cta-email"
                className="btn-gradient text-white font-semibold px-8 rounded-full border-0 shadow-lg"
                onClick={() => window.location.href = `mailto:${personalInfo.email}`}
              >
                <Mail className="w-5 h-5 mr-2" />
                Send an Email
              </Button>
              <Button
                size="lg"
                variant="outline"
                data-testid="contact-cta-linkedin"
                className="border-2 border-[#8b5a3c]/40 text-[#6b4226] hover:bg-gradient-to-r hover:from-[#b88865]/10 hover:to-[#6b4226]/10 hover:border-[#8b5a3c]/60 font-semibold px-8 rounded-full transition-all duration-300"
                onClick={() => window.open(personalInfo.linkedin, '_blank', 'noopener,noreferrer')}
              >
                <Linkedin className="w-5 h-5 mr-2" />
                LinkedIn Profile
              </Button>
            </div>

            {/* Bottom tagline */}
            <div className="mt-10 pt-8 border-t border-[#e0d0b8] text-center">
              <div className="flex items-center justify-center gap-2 text-[#737373]">
                <Zap className="w-5 h-5 text-[#8b5a3c]" />
                <p className="text-sm">
                  <span className="text-[#2d2a26] font-semibold">Quick response guaranteed</span> - Usually within 24 hours
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default ContactNew;
